import { useState } from "react"
import { Link, useSearchParams } from "react-router"

type Status = "idle" | "loading" | "success" | "error"

export function NewsletterUnsubscribePage() {
  const [searchParams] = useSearchParams()
  const subscriberId = searchParams.get("subscriberId")
  const token = searchParams.get("token")
  const signed = Boolean(subscriberId && token)
  const [email, setEmail] = useState("")
  const [status, setStatus] = useState<Status>("idle")

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (status === "loading") return
    setStatus("loading")

    try {
      const response = await fetch("/api/public/unsubscribe", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(signed ? { subscriberId, token } : { email }),
      })
      const result = (await response.json()) as { ok?: boolean }
      if (!response.ok || !result.ok) throw new Error("Could not unsubscribe")
      setStatus("success")
    } catch {
      setStatus("error")
    }
  }

  return (
    <section className="bg-cream-50 py-12 sm:py-16 lg:py-24">
      <div className="public-container">
        <div className="mx-auto max-w-xl rounded-[24px] border border-gold-500 bg-white p-7 sm:p-10">
          {status === "success" ? (
            <div className="text-center">
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-forest-700">Newsletter preferences</p>
              <h1 className="mt-4 font-display text-3xl font-bold text-forest-950 sm:text-4xl">You&apos;re unsubscribed.</h1>
              <p className="mt-4 text-base leading-7 text-ink/70">You will no longer receive Redempta newsletter updates.</p>
              <Link to="/" className="mt-8 inline-flex rounded-full bg-forest-950 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-forest-800">
                Return to Redempta
              </Link>
            </div>
          ) : (
            <>
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-forest-700">Newsletter preferences</p>
              <h1 className="mt-4 font-display text-3xl font-bold text-forest-950 sm:text-4xl">Unsubscribe from updates</h1>
              <p className="mt-4 text-base leading-7 text-ink/70">
                {signed
                  ? "Confirm below to stop receiving newsletter updates from Redempta."
                  : "Enter your email address and we'll remove it from the Redempta newsletter list."}
              </p>

              <form onSubmit={submit} className="mt-8 space-y-5">
                {!signed && (
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-ink">Email address</span>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="you@example.com"
                      disabled={status === "loading"}
                      className="w-full rounded-xl border border-gold-500 bg-cream-50 px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink/40 focus:border-forest-700 disabled:opacity-60"
                    />
                  </label>
                )}
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full rounded-full bg-forest-950 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-forest-800 disabled:opacity-60"
                >
                  {status === "loading" ? "Updating..." : "Unsubscribe me"}
                </button>
              </form>
              {status === "error" && (
                <p className="mt-4 text-sm text-red-600">
                  {signed
                    ? "This unsubscribe link is invalid or has expired. Please try using your email address instead."
                    : "We could not update your subscription. Please try again."}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  )
}
